import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import { getUuid } from '@front10/helpers/dist/uuid';
import { ariaDescribeDictionary } from './FareItemCheckRestriction';
import { useTracking } from '../../../../_integrations/everymundo/tracking/TrackingContext.tsx';

import Button from '../../../elements/Button';
import Text from '../../../elements/Text';
import Icon from '../../../elements/Icon';

/**
 * ## Example
 * ```js
<FareItem
  tileBodyPadding="3"
  tileFooterPaddingTop="5"
  tileFooterTextAlign="center"
/>
 * ```
 */
const FareItemCallToAction = (props) => {
  const {
    callToActionColor,
    showCallToActionSubText,
    callToAction,
    labelStyles,
    subTextLabelStyles,
    icon,
    showIcon,
    showLoading,
    iconStyles,
    showCallToActionTextMin,
    tabIndex,
    onClick,
    onKeyDown,
    labelKey,
    labelKeyMin,
    labelKeySubText,
    onBlur,
    enabledDataForSR = false,
    ariaLabelledBy,
    focusReferences,
    onlyOneAriaLabelledBy = false,
    index,
    availableAtoms
  } = props;

  const { translate, direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const id = getUuid();
  const [isLoading, setIsLoading] = useState(false);
  const { fareItemInteractionType } = useTracking();
  const displayLoadingIcon =
    showLoading !== undefined
      ? showLoading
      : fareItemInteractionType === 'deeplink'
      ? true
      : showLoading;

  const handleCallToAction = (e) => {
    onClick(
      e,
      showCallToActionTextMin ? translate(labelKeyMin, 'Book') : translate(labelKey, 'Book now')
    );
  };

  const ariaDescribedBy =
    Array.isArray(availableAtoms) &&
    availableAtoms
      ?.filter((i) => i !== 'FareItemCheckRestriction')
      ?.map((i) => {
        return `${ariaDescribeDictionary[i]}${index} `;
      });

  const handleClick = (e) => {
    if (!isLoading) {
      displayLoadingIcon && setIsLoading(true);
      handleCallToAction(e);
    }
  };

  return (
    <Button
      className={`call-to-action-button ${className}`}
      dataReactCmpOwner="fareItemCallToAction"
      color={callToActionColor}
      style={style}
      onClick={handleClick}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      focusReferences={focusReferences}
      {...callToAction}
      tabIndex={tabIndex ? null : -1}
      dataTestId="fareItemCallToAction"
      id={`fare-item-call-to-action-${index}`}
      ariaDescribedBy={enabledDataForSR ? ariaDescribedBy.join(' ') : undefined}
      ariaLabelledBy={
        ariaLabelledBy
          ? onlyOneAriaLabelledBy
            ? `${ariaLabelledBy}`
            : `${ariaLabelledBy} text-${id} ${showCallToActionSubText ? `subText-${id}` : ''}`
          : null
      }
    >
      {displayLoadingIcon && isLoading ? (
        <>
          <Icon {...iconStyles} display="inline-block" align="middle" icon="spinner" />
        </>
      ) : (
        <>
          {showIcon && (
            <Text floatLeft dataReactCmpOwner="fareItemCallToAction">
              <Icon {...iconStyles} icon={icon} dataReactCmpOwner="fareItemCallToAction" />
            </Text>
          )}
          <Text
            id={`text-${id}`}
            {...labelStyles}
            ariaHidden
            dataReactCmpOwner="fareItemCallToAction"
          >
            {showCallToActionTextMin
              ? translate(labelKeyMin, 'Book')
              : translate(labelKey, 'Book now')}
          </Text>
          {showCallToActionSubText && (
            <Text
              id={`subText-${id}`}
              {...subTextLabelStyles}
              dataReactCmpOwner="fareItemCallToAction"
            >
              {translate(labelKeySubText, labelKeySubText)}
            </Text>
          )}
        </>
      )}
    </Button>
  );
};

FareItemCallToAction.propTypes = {
  /**
   * Color of the button.
   */
  callToActionColor: PropTypes.string,
  /**
   * onClick
   */
  onClick: PropTypes.func,
  /**
   * onClick
   */
  onKeyDown: PropTypes.func,
  /**
   * Show call to action miniText.
   */
  showCallToActionTextMin: PropTypes.bool,
  /**
   * Show call to action subText.
   */
  showCallToActionSubText: PropTypes.bool,
  /**
   * labelStyles props for using into withStyle.
   */
  labelStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * subTextLabelStyles props for using into withStyle.
   */
  subTextLabelStyles: PropTypes.objectOf(PropTypes.any),
  callToAction: PropTypes.objectOf(PropTypes.any),
  /**
   * Icon
   */
  icon: PropTypes.string,
  /**
   * Show Icon
   */
  showIcon: PropTypes.bool,
  /**
   * iconStyles props for using into withStyle.
   */
  iconStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Define if the callToAction will have focus for Screen Readers
   */
  tabIndex: PropTypes.bool,
  /**
   * Function to handle focus renference of the component
   */
  focusReferences: PropTypes.func,
  labelKey: PropTypes.string,
  labelKeyMin: PropTypes.string,
  labelKeySubText: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  onlyOneAriaLabelledBy: PropTypes.bool
};
FareItemCallToAction.defaultProps = {
  callToActionColor: 'primary',
  onClick: () => {},
  onKeyDown: () => {},
  showCallToActionTextMin: false,
  showCallToActionSubText: false,
  onlyOneAriaLabelledBy: false,
  labelStyles: {},
  subTextLabelStyles: {},
  callToAction: {},
  icon: '',
  showIcon: false,
  iconStyles: {},
  tabIndex: true,
  labelKey: 'fare_call_to_action_label',
  labelKeyMin: 'fare_call_to_action_label_min',
  labelKeySubText: 'fare_call_to_action_extra_info_label',
  ariaLabelledBy: null,
  focusReferences: null
};

FareItemCallToAction.componentName = 'FareItemCallToAction';

export default FareItemCallToAction;
